import { useRef, useState } from "react";
import type { InlineTypeWidgetComponentProps } from "@easyblocks/core";
import { useEditorContext } from "@easyblocks/editor";
import { MediaBrowserModal } from "../components/modals/MediaBrowserModal";

function isProbablyImageUrl(url: string): boolean {
  // Accept normal URLs and data URLs (local fallback uses data URLs).
  if (/^data:image\//i.test(url)) return true;
  return /\.(png|jpe?g|webp|gif|avif|bmp|svg)(\?.*)?$/i.test(url);
}

function dotNotationGet(obj: unknown, path: string): unknown {
  if (!path) return obj;
  return path.split(".").reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function findClosestComponentId(editorContext: any): string {
  const focussedField = editorContext?.focussedField;
  const formValues = editorContext?.form?.values;
  if (!Array.isArray(focussedField) || focussedField.length !== 1 || !formValues) {
    return "";
  }

  const selectedPath = String(focussedField[0] ?? "").trim();
  if (!selectedPath) return "";

  const pathParts = selectedPath.split(".");
  for (let i = pathParts.length; i > 0; i -= 1) {
    const candidate = dotNotationGet(
      formValues,
      pathParts.slice(0, i).join(".")
    ) as { _component?: string } | undefined;
    if (
      candidate &&
      typeof candidate === "object" &&
      typeof candidate._component === "string" &&
      candidate._component.trim() !== ""
    ) {
      return candidate._component.trim();
    }
  }

  return "";
}

export function ImageUploadWidget(props: InlineTypeWidgetComponentProps<string>) {
  const editorContext = useEditorContext();
  const selectedValue = String(props?.value ?? "");
  const previewContainerBaseClass = "border rounded bg-light ratio ratio-1x1";
  const previewContainerStyle = { maxWidth: "100%" };
  const previewImageClass = "object-fit-contain top-50 start-50 translate-middle img-fluid";

  const instanceIdRef = useRef<string>(`media-browser-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const componentId = findClosestComponentId(editorContext);
  const uploadContext = componentId === "HeroImageSection" ? "hero" : "image";

  function handlePick(url: string) {
    const clean = String(url ?? "").trim();
    if (!clean) return;
    props.onChange(clean);
    setIsModalOpen(false);
  }

  return (
    <div
      className="d-flex flex-column gap-2 w-100 overflow-hidden"
      style={{ maxWidth: "100%", minWidth: 0 }}
    >
      {/* Row 1: Preview (full width) */}
      <div
        className={`${previewContainerBaseClass} w-100`}
        aria-label="Selected image preview"
        style={previewContainerStyle}
      >
        {selectedValue && isProbablyImageUrl(selectedValue) ? (
          <img
            src={selectedValue}
            alt=""
            className={previewImageClass}
          />
        ) : (
          <div className="d-flex align-items-center justify-content-center w-100 h-100">
            <span className="text-body-secondary small text-center">Select or <br/> upload an image</span>
          </div>
        )}
      </div>

      {/* Row 2: Filename / path */}
      <div className="fs-12 text-body-secondary text-wrap text-break" title={selectedValue || ""}>
        {(() => {
          if (!selectedValue) return "No image selected";
          if (/^data:image\//i.test(selectedValue)) return "Embedded image (data URL)";
          try {
            const u = new URL(selectedValue, window.location.origin);
            return u.pathname + (u.search || "");
          } catch {
            return selectedValue;
          }
        })()}
      </div>

      {/* Row 3: Actions */}
      <div className="d-flex flex-wrap gap-2 align-items-center w-100">
        <button
          type="button"
          className="btn btn-sm btn-primary w-100 text-wrap"
          style={{ maxWidth: "100%" }}
          onClick={() => setIsModalOpen(true)}
        >
          Browse or Upload
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
          title="Clear"
          aria-label="Clear"
          onClick={() => props.onChange("")}
          disabled={!selectedValue}
        >
          Remove
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
            className="ms-1"
          >
            <path d="M5.5 5.5A.5.5 0 0 1 6 6v7a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 0A.5.5 0 0 1 9 6v7a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v7a.5.5 0 0 0 1 0V6Z"/>
            <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1 0-2H5.5l.5-.5A1 1 0 0 1 6.707 1h2.586a1 1 0 0 1 .707.293l.5.5H14a1 1 0 0 1 .5 1.207ZM4 4v9a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4H4Z"/>
          </svg>
        </button>
      </div>

      <MediaBrowserModal
        isOpen={isModalOpen}
        instanceId={instanceId}
        onClose={() => setIsModalOpen(false)}
        onPick={handlePick}
        uploadContext={uploadContext}
      />
    </div>
  );
}


